"use client";
import Image from "next/image";
import React, { Suspense, useEffect, useState } from "react";
import Link from "next/link"; // Added Link for navigation
import { FaLightbulb, FaCogs, FaUsers, FaPiggyBank, FaLeaf } from "react-icons/fa";
import { FiShield, FiUser, FiPenTool, FiHelpCircle } from "react-icons/fi";
import { FaMoneyCheckDollar } from "react-icons/fa6";
import { motion } from "framer-motion";
import { IconType } from "react-icons";

import ScrollUp from "@/components/Common/ScrollUp";
import TrustIndicators from "@/components/About/trustindicators";
import { HeroSectionhome } from "./herohome";
import { HeroSection } from "./about/HeroSection";
import NewsSection from "./news/newssection";
import Corousel from "@/components/Common/corousel";
import CurrentStatusSection from "@/components/About/currentstatus";
import TestimonialSection from "@/components/Common/TestimonialSection";
import SocialContributions from "./socialContributions";

// Clean color palette - Only Deep Teal Green and White
const colors = {
  primary: "#395750",
  primaryDark: "#2A423C",
  primaryLight: "#4B6B63",
  white: "#FFFFFF",
  whiteOff: "#F8F9FA",
  textDark: "#1A2E29",
};

export default function Home() {
  const [mounted, setMounted] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hoveredOfferIndex, setHoveredOfferIndex] = useState<number | null>(null);

  const text = "Welcome to Yegna Microfinance";

  // Handle mounting and typewriter effect
  useEffect(() => {
    setMounted(true);
    
    // Cleanup function
    return () => {
      setMounted(false);
    };
  }, []);

  useEffect(() => {
    if (mounted && currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setCurrentIndex((prev) => prev + 1);
      }, 100);
      return () => clearTimeout(timeout);
    }
  }, [mounted, currentIndex, text.length]);

  // Reset typewriter effect when component unmounts
  useEffect(() => {
    return () => {
      setCurrentIndex(0);
    };
  }, []);

  type Service = {
    title: string;
    description: string;
    icon: IconType;
  };

  const services: Service[] = [
    {
      title: "Software Development",
      description: "Tailored software solutions designed to meet your unique business requirements.",
      icon: FiPenTool,
    },
    {
      title: "IT Support & Consulting",
      description: "Expert advice to streamline your IT strategies and optimize operations.",
      icon: FiHelpCircle,
    },
    {
      title: "Representatives",
      description: "Support your business with friendly and professional sales, call center, & Custom-tailored resources.",
      icon: FiUser,
    },
    {
      title: "Cybersecurity",
      description: "Protect your business from cyber threats with our comprehensive security solutions.",
      icon: FiShield,
    },
    {
      title: "Data Entry",
      description: "Accurate and efficient data management services including document digitization and database management.",
      icon: FiShield,
    },
    {
      title: "Digital & Social Media Marketing",
      description: "Handling online presence and brand reputation through social media content creation, campaign management, and analytics",
      icon: FiShield,
    },
  ];

  type Feature = {
    title: string;
    description: string;
    icon: IconType;
  };

  const features: Feature[] = [
    {
      title: "Affordable & Accessible Loans",
      description: "We provide flexible loan options tailored for small businesses, entrepreneurs, and individuals, ensuring financial inclusion for all.",
      icon: FaMoneyCheckDollar,
    },
    {
      title: "Tailored and High Interest Earning Deposit Services",
      description: "We offer customized, high-yield deposit solutions designed for small businesses, entrepreneurs, and individuals, promoting financial inclusion and empowering all clients to achieve their financial goals.",
      icon: FaPiggyBank,
    },
    {
      title: "Empowering Communities",
      description: "Through financial literacy programs and economic empowerment initiatives, we help communities build sustainable growth and self-reliance.",
      icon: FaLightbulb,
    },
    {
      title: "Green Financing Service",
      description: "It focuses on providing financial products and services that support environmentally sustainable projects, climate resilience, and clean energy initiatives.",
      icon: FaLeaf,
    },
    {
      title: "Scalable Financial Services",
      description: "Our microfinance services adapt to your needs, offering scalable options that grow alongside your business and personal financial goals.",
      icon: FaCogs,
    },
    {
      title: "Customer-Centric Approach",
      description: "We prioritize customer satisfaction by offering personalized financial services, quick processing times, and dedicated support.",
      icon: FaUsers,
    },
  ];

  const offerItems = [
    {
      title: "Loan Service",
      description: "We provide flexible business, personal, and emergency loans tailored to your needs.",
      icon: "M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
    },
    {
      title: "Saving & Time Deposit",
      description: "Secure your future with high-interest savings and safe deposit accounts.",
      icon: "M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
    },
    {
      title: "Training & Consultancy",
      description: "Training for Micro-Businesses",
      icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
    },
    {
      title: "LANA Interest-Free",
      description: "Ethical financing ∙ Interest Free ∙ Full compliance",
      icon: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
    },
  ];

  // If not mounted, render nothing or a loader to prevent hydration issues
  if (!mounted) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.whiteOff }}>
        <div className="text-center">
          <div className="animate-pulse" style={{ color: colors.primary }}>
            Loading...
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* <ScrollUp /> */}

      {/* Hero Section */}
      <section
        id="home"
        className="relative z-10 font-orbitron"
        style={{
          backgroundColor: colors.whiteOff,
          boxShadow: `0px 0px 40px ${colors.primary}20`,
        }}
      >
        <HeroSectionhome />
        <div className="absolute bottom-0 left-0 z-[-1] opacity-20 lg:opacity-50">
          <svg
            width="364"
            height="201"
            viewBox="0 0 364 201"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5.88928 72.3303C33.6599 66.4798 101.397 64.9086 150.178 105.427C211.155 156.076 229.59 162.093 264.333 166.607C299.076 171.12 337.718 183.657 362.889 212.24"
              stroke={colors.primary}
              strokeWidth="2"
            />
            <defs>
              <linearGradient
                id="paint0_linear_25:218"
                x1="184.389"
                y1="69.2405"
                x2="184.389"
                y2="212.24"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor={colors.primary} stopOpacity="0.5" />
                <stop offset="1" stopColor={colors.primary} stopOpacity="0" />
              </linearGradient>
            </defs>
          </svg>
        </div>
      </section>

      
      {/* <section className="pt-8 pb-2 md:pt-12 md:pb-4 lg:pt-16 lg:pb-6" style={{ backgroundColor: colors.whiteOff }}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative mx-auto flex max-w-6xl overflow-hidden rounded-sm bg-white shadow-sm"
          >
          
            <div 
              className="w-2 shrink-0 md:w-3" 
              style={{ backgroundColor: colors.primary }}
            ></div>

            
            <div className="flex flex-col p-5 md:p-8 lg:p-10">
              <h2 
                className="mb-3 font-orbitron text-2xl font-bold md:mb-5 md:text-3xl"
                style={{ color: colors.primary }}
              >
                About us
              </h2>

              
              <div 
                className="font-poppins text-sm leading-relaxed md:text-base"
                style={{ color: colors.textDark }}
              >
                <p className="mb-4 text-justify">
                  Yegna Microfinance Institution S.C. was established to support national economic growth by providing customized financial products. It primarily serves micro, small, and medium businesses, addressing financial gaps for low-income entrepreneurs while ensuring profitability and sustainability.
                </p>
                <p className="text-justify">
                  Licensed by the National Bank of Ethiopia, Yegna Microfinance started operations in 2022 with an initial capital of Birr 40 Million. The institution is backed by committed and experienced shareholders, working closely with governmental and financial organizations to expand financial inclusivity.
                </p>
              </div>

              
              <div className="mt-4 flex justify-end md:mt-6">
                <Link 
                  href="/aboutyegna/" 
                  className="group flex items-center font-orbitron text-base font-bold transition-colors md:text-lg"
                  style={{ color: colors.primary }}
                >
                  Read more
                  <span className="ml-2 transition-transform duration-300 group-hover:translate-x-1">
                    &rarr;
                  </span>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section> */}
      <NewsSection />


      {/* WHAT WE OFFER SECTION - Minimized bottom padding */}
      <section className="relative overflow-hidden pt-2 pb-4 md:pt-4 md:pb-6 lg:pt-6 lg:pb-8" style={{ backgroundColor: colors.white }}>
        <div className="absolute inset-0 -rotate-3 -skew-y-6 transform-gpu">
          <div
            className="h-full w-full opacity-10 mix-blend-soft-light"
            style={{
              backgroundImage: `linear-gradient(45deg, ${colors.primary}20 25%, transparent 25%, transparent 75%, ${colors.primary}20 75%, ${colors.primary}20), linear-gradient(45deg, ${colors.primary}20 25%, transparent 25%, transparent 75%, ${colors.primary}20 75%, ${colors.primary}20)`,
              backgroundSize: "60px 60px",
              backgroundPosition: "0 0, 30px 30px",
            }}
          />
        </div>

        <div className="absolute -inset-8 -rotate-45">
          <div 
            className="animate-pulse-slow absolute inset-0 -z-10 rounded-xl blur-xl"
            style={{
              background: `linear-gradient(to bottom right, ${colors.primary}15, ${colors.primary}05, ${colors.primaryDark}10)`
            }}
          />
        </div>

        <div 
          className="absolute -right-12 -top-12 h-64 w-64 rounded-full blur-xl"
          style={{
            background: `radial-gradient(circle, ${colors.primary}10, transparent)`
          }}
        />
        <div 
          className="absolute -bottom-12 -left-12 h-64 w-64 rounded-full blur-xl"
          style={{
            background: `radial-gradient(circle, ${colors.primaryDark}10, transparent)`
          }}
        />

        <motion.h2
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mb-6 text-center font-orbitron text-2xl font-bold md:text-3xl lg:mb-8 lg:text-4xl"
        >
          <span 
            className="bg-gradient-to-r bg-clip-text text-transparent"
            style={{
              backgroundImage: `linear-gradient(to right, ${colors.primaryDark}, ${colors.primary}, ${colors.primaryLight})`
            }}
          >
            What We Offer
          </span>
        </motion.h2>

        <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-4 px-4 sm:grid-cols-2 lg:grid-cols-4">
          {offerItems.map((item, index) => {
            const isHovered = hoveredOfferIndex === index;
            
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                whileHover={{ 
                  y: -5,
                  transition: { duration: 0.2, ease: "easeOut" }
                }}
                onMouseEnter={() => setHoveredOfferIndex(index)}
                onMouseLeave={() => setHoveredOfferIndex(null)}
                className="relative h-full min-h-[140px] w-full overflow-hidden rounded-xl border shadow-lg transition-all duration-300 hover:shadow-2xl cursor-pointer"
                style={{ 
                  borderColor: `${colors.primary}20`,
                  backgroundColor: isHovered ? colors.primary : colors.white
                }}
              >
                <motion.div 
                  className="absolute inset-0"
                  style={{ backgroundColor: colors.primary }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: isHovered ? 1 : 0 }}
                  transition={{ duration: 0.3 }}
                />
                
                <motion.div
                  className="absolute -inset-full top-0 h-full w-1/2 z-5 block transform -skew-x-12"
                  style={{
                    background: `linear-gradient(90deg, transparent, ${colors.white}20, transparent)`,
                  }}
                  initial={{ left: "-150%" }}
                  animate={{ left: isHovered ? "150%" : "-150%" }}
                  transition={{ duration: 0.6, ease: "easeInOut" }}
                />

                <div className="relative z-10 p-5">
                  <div className="flex flex-col items-start">
                    <motion.div 
                      className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl transition-all duration-300"
                      style={{ 
                        backgroundColor: isHovered ? `${colors.white}20` : `${colors.primary}10`,
                      }}
                      whileHover={{ 
                        rotate: [0, -5, 5, -3, 3, 0],
                        transition: { duration: 0.5 }
                      }}
                    >
                      <motion.svg
                        className="h-6 w-6 transition-all duration-300"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        animate={{ 
                          stroke: isHovered ? colors.white : colors.primary,
                          scale: isHovered ? 1.1 : 1
                        }}
                        transition={{ duration: 0.2 }}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d={item.icon}
                        />
                      </motion.svg>
                    </motion.div>
                    
                    <div className="w-full">
                      <motion.h3 
                        className="mb-2 font-orbitron text-lg font-semibold md:text-xl"
                        style={{ color: isHovered ? colors.white : colors.primary }}
                        animate={{ 
                          color: isHovered ? colors.white : colors.primary,
                          x: isHovered ? 3 : 0
                        }}
                        transition={{ duration: 0.2 }}
                      >
                        {item.title}
                      </motion.h3>
                      
                      <motion.p 
                        className="font-poppins text-sm leading-relaxed"
                        style={{ color: isHovered ? colors.white : colors.textDark }}
                        animate={{ 
                          color: isHovered ? colors.white : colors.textDark,
                          opacity: isHovered ? 1 : 0.95
                        }}
                        transition={{ duration: 0.2 }}
                      >
                        {item.description}
                      </motion.p>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* NEW LAYOUT FOR "YOUR #1 CHOICE" SECTION - Minimized top padding, smaller image, centered grid */}
      <section className="relative overflow-hidden pt-4 pb-16 md:pt-6 md:pb-24 lg:pt-8 lg:pb-32" style={{ backgroundColor: colors.whiteOff }}>
        {/* Background Decorative Elements */}
        <div className="absolute inset-0 z-0 opacity-10">
          {[...Array(50)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute h-0.5 w-0.5 rounded-full"
              style={{ backgroundColor: `${colors.primary}40` }}
              initial={{ opacity: 0 }}
              animate={{
                opacity: [0, 0.3, 0],
                x: Math.random() * 100 + "%",
                y: Math.random() * 100 + "%",
              }}
              transition={{
                duration: 2 + Math.random() * 5,
                repeat: Infinity,
                ease: "linear",
              }}
            />
          ))}
        </div>

        <div className="absolute -inset-24 -rotate-45">
          <div 
            className="animate-pulse-slow absolute inset-0 -z-10 rounded-xl blur-[100px]"
            style={{
              background: `linear-gradient(to bottom right, ${colors.primary}10, ${colors.primary}05)`
            }}
          />
        </div>

        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Header */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-4 text-center font-orbitron text-3xl font-bold md:mb-6 md:text-4xl lg:text-5xl"
            style={{ color: colors.textDark }}
          >
            <span 
              className="animate-text-shimmer bg-clip-text text-transparent"
              style={{
                backgroundImage: `linear-gradient(to right, ${colors.primaryDark}, ${colors.primary}, ${colors.primaryLight})`
              }}
            >
              Your #1 Choice
            </span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mx-auto mb-10 max-w-3xl text-center font-poppins text-base leading-relaxed md:text-lg"
            style={{ color: colors.textDark }}
          >
            At Yegna Microfinance, we are committed to providing accessible
            financial services that promote economic independence. Our Services
            empower individuals and businesses across Ethiopia by fostering
            sustainable growth.
          </motion.p>

          {/* Centered Layout: Image on top (smaller), Features in a 3-column grid below */}
          <div className="flex flex-col items-center">
            
            {/* Smaller Centered Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative mb-12 h-64 w-full max-w-2xl overflow-hidden rounded-[2rem] border shadow-xl md:h-80"
              style={{ borderColor: `${colors.primary}20` }}
            >
              <Image
                src="/images/about/whychoose.jpg"
                alt="Empowering Communities"
                fill
                className="object-cover"
                sizes="(max-width: 768px) 100vw, 600px"
                priority
              />
              <div 
                className="absolute inset-0"
                style={{
                  background: `linear-gradient(to top, ${colors.primary}40, transparent)`
                }}
              />
            </motion.div>

            {/* Features Grid (3 columns on desktop) */}
            <div className="grid w-full grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -5, boxShadow: `0 10px 30px ${colors.primary}15` }}
                  className="group relative flex flex-col rounded-2xl border bg-white p-6 shadow-sm transition-all duration-300"
                  style={{ borderColor: `${colors.primary}15` }}
                >
                  <div 
                    className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl transition-colors duration-300 group-hover:bg-opacity-20"
                    style={{ backgroundColor: `${colors.primary}10` }}
                  >
                    <feature.icon 
                      className="text-2xl transition-transform duration-300 group-hover:scale-110"
                      style={{ color: colors.primary }}
                    />
                  </div>
                  
                  <h3 
                    className="mb-2 font-orbitron text-lg font-semibold"
                    style={{ color: colors.primaryDark }}
                  >
                    {feature.title}
                  </h3>
                  
                  <p 
                    className="font-poppins text-sm leading-relaxed"
                    style={{ color: colors.textDark }}
                  >
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>

          </div>
        </div>

        {/* Grid Pattern Overlay */}
        <div className="pointer-events-none absolute inset-0 z-0">
          <div 
            className="h-full w-full"
            style={{
              backgroundImage: `linear-gradient(to right, ${colors.primary}05 1px, transparent 1px), linear-gradient(to bottom, ${colors.primary}05 1px, transparent 1px)`,
              backgroundSize: "24px 24px"
            }}
          />
        </div>
      </section>

      <Suspense fallback={<div className="min-h-[400px]" style={{ backgroundColor: colors.white }}>Loading...</div>}>
        <SocialContributions />
      </Suspense>
      
      <CurrentStatusSection />
      
      <Corousel />
      <TestimonialSection />

      <div className="relative w-full">
        <div 
          className="h-px w-full bg-gradient-to-r from-transparent to-transparent"
          style={{ background: `linear-gradient(to right, transparent, ${colors.primary}30, transparent)` }}
        />
      </div>
    </>
  );
}